import { CCol, CFormGroup, CRow } from "@coreui/react";
import { get } from "lodash";
import { BasicInput, BasicNumberInput, BasicSelect, ErrorText } from "src/components";
import DatePicker from "src/components/DatePicker/DatePicker";
import MultiSelect from "src/components/MultiSelect/MultiSelect";
import SingleInput from "src/components/SingleInput/SingleInput";
import { SCOPE_3_INCLUDES } from "./ghg-scope3";

const CURRENT_YEAR = new Date().getFullYear();
const LAST_20_YEARS = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR - i);


export const DATA_COLLECTION = ({ onChange, values = {}, errors = {} }) => ({

    "environment": [
        {
            component: (
                <SingleInput
                    Component={BasicSelect}
                    name="assessed-ghg"
                    {...{ onChange, values, errors }}
                    placeholder="Select option..."
                    options={[
                        { label: "Yes", value: true },

                        { label: "No", value: false },
                    ]}
                />),
            label: "Have you assessed your organisation's GHG emissions?",
            name: "assessed-ghg"
        },
        {
            label: "What was the most recent year of assessment?",
            name: "ghg-assessment-year",
            component: (
                <SingleInput
                    Component={BasicSelect}
                    name="ghg-assessment-year"
                    {...{ onChange, values, errors }}
                    placeholder="Select option..."
                    options={LAST_20_YEARS.map((year) => ({
                        label: year,
                        value: year,
                    }))}
                />)
        },
        {
            label: "What are your GHG emissions for Scope 1? (tCO2e)",
            name: "ghg-scope1",
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="ghg-scope1"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />)
        },
        {
            label: "What are your GHG emissions for Scope 2? (tCO2e)",
            name: "ghg-scope2",
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="ghg-scope2"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />)
        },
        {
            label: "What are your GHG emissions for Scope 3? (tCO2e)",
            name: "ghg-scope3",
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="ghg-scope3"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />)
        },
        {
            label: "What was included in your scope 3 emissions?",
            name: "ghg-scope3-includes",
            component: (
                <MultiSelect
                    name="ghg-scope3-includes"
                    onChange={e => onChange({ target: { name: "ghg-scope3-includes", value: e } })}
                    defaultValue={values["ghg-scope3-includes"]}
                    options={SCOPE_3_INCLUDES}
                />
            )
        }

    ],
    "community": [{
        label: "Supplier diversity",
    },
    {
        component: (
            <SingleInput
                Component={BasicNumberInput}
                name="suppliers-minority"
                {...{ onChange, values, errors }}
                placeholder="Enter percentage..."
                min={0}
                max={100}
                allowNegative={false}
                suffix={" %"}

            />
        ),
        label:
            "What percentage of your suppliers are owned by individuals from underrepresented groups?",
        name: "suppliers-minority"
    },],
    "employees": [
        {
            label: "Workforce numbers",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="fte"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />
            ),
            label:
                "What was the full-time equivalent number (FTE) of employees in your organization?",
            name: "fte"
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="employee-number"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />
            ),
            label:
                "What was the number of individuals employed by your organization? (if different from FTE)",
            name: "employee-number"
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="fte-turnover"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}
                />
            ),
            label: "What was the percentage of employee turnover (FTE)?",
            name: "fte-turnover"
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="employee-turnover"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What was the percentage of employee turnover (individuals - if different from FTE)?",
            name: "employee-turnover"
        },

        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="non-employees"
                    {...{ onChange, values, errors }}
                    placeholder="Enter number..."
                    min={0}
                />
            ),
            label:
                "How many non-employee workers do you have directly contracted in roles - e.g. contractors, self-employed workers? (as individuals)",
            name: "non-employees"
        },
        {
            component: (
                <SingleInput
                    Component={BasicSelect}
                    name="non-employees-included"
                    {...{ onChange, values, errors }}
                    placeholder="Select option..."
                    options={[
                        { label: "Yes, in both", value: "both" },
                        { label: "Yes, in FTE only", value: "fte-only" },
                        {
                            label: "Yes, in individual employees only",
                            value: "individual-only",
                        },
                        { label: "No", value: "no" },
                    ]}
                />
            ),
            label:
                "Are these non-employed workers included in your FTE & individual employee numbers above?",
            name: "non-employees-included"
        },
        {
            label: "Workforce demographics",
        },
        {
            component: (
                <div>
                    <CRow style={{ marginLeft: "-15px" }}>
                        <CCol className="col-12 col-md-4 mb-3 ">
                            <p className="mb-1">Female</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"workforce-gender.female"}
                                defaultValue={values["workforce-gender"]?.female}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                        <CCol className="col-12 col-md-4 mb-3 ">
                            <p className="mb-1">Male</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"workforce-gender.male"}
                                defaultValue={values["workforce-gender"]?.male}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                        <CCol className="col-12 col-md-4 ">
                            <p className="mb-1">Other</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"workforce-gender.other"}
                                defaultValue={values["workforce-gender"]?.other}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                    </CRow>
                    <ErrorText value={errors["workforce-gender"]} />
                </div>
            ),
            label: "What percentage of your workforce identifies as...?",
            name: "workforce-gender"
        },
        {
            label: "Leadership representation",
        },
        {
            component: (
                <div>
                    <CRow style={{ marginLeft: "-15px" }}>
                        <CCol className="col-12 col-md-4 mb-3 ">
                            <p className="mb-1">Female</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"leadership-gender.female"}
                                defaultValue={values["leadership-gender"]?.female}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                        <CCol className="col-12 col-md-4 mb-3 ">
                            <p className="mb-1">Male</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"leadership-gender.male"}
                                defaultValue={values["leadership-gender"]?.male}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                        <CCol className="col-12 col-md-4 ">
                            <p className="mb-1">Other</p>
                            <BasicNumberInput
                                allowNegative={false}
                                min={0}
                                max={100}
                                name={"leadership-gender.other"}
                                defaultValue={values["leadership-gender"]?.other}
                                onChange={onChange}
                                placeholder={"Enter percentage..."}
                                suffix={" %"}

                            />
                        </CCol>
                    </CRow>
                    <ErrorText value={errors["leadership-gender"]} />
                </div>
            ),
            label:
                "What percentage of your executive leadership team identifies as...?",
            name: "leadership-gender"
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="leadership-minority"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    allowNegative={false}
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What percentage of your executive leadership team identifies as belonging to racial or ethnic minority groups?",
            name: "leadership-minority"
        },
        {
            label: "Accessibility and inclusion",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="leadership-disability"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What percentage of your workforce has disclosed a disability or accessibility need?",
            name: "leadership-disability"
        },
        {
            label: "LGBTQ+ representation",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="leadership-lgbtq"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label: "What percentage of your workforce identifies as LGBTQ+?",
            name: "leadership-lgbtq"
        },
        {
            label: "Gender pay equity",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="pay-gap"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What is the gender pay gap within your organisation, expressed as a percentage?",
            name: "pay-gap"
        },
        {
            component: (

                <CFormGroup >

                    <DatePicker
                        maxDate={new Date()}
                        name="pay-gap-date"
                        onChange={onChange}
                        value={values["pay-gap-date"]}
                        className="w-100"
                        placeholderText={"Select date..."}
                        isClearable
                    />
                    {errors && <ErrorText value={get(errors, "pay-gap-date")} />}
                </CFormGroup>
            ),
            label: "When did we last assess our gender pay gap?",
            name: "pay-gap-date"
        },
        {
            label: "Training and development",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="di-training"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What percentage of your workforce has participated in diversity and inclusion training programs in the last year?",
            name: "di-training"
        },
        {
            label: "Ethnic and racial diversity",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="workforce-minority"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                "What percentage of your workforce identifies as belonging to racial or ethnic minority groups?",
            name: "workforce-minority"
        },
        {
            component: (
                <SingleInput
                    Component={BasicInput}
                    name="minority-classification"
                    {...{ onChange, values, errors }}
                />
            ),
            label:
                " What classification have you used to define your racial/ethic minority groups?",
            name: "minority-classification"
        },
        {
            label: "Employee satisfaction",
        },
        {
            component: (
                <SingleInput
                    Component={BasicNumberInput}
                    name="employee-satisfaction"
                    {...{ onChange, values, errors }}
                    placeholder="Enter percentage..."
                    min={0}
                    max={100}
                    suffix={" %"}

                />
            ),
            label:
                " What is the overall employee satisfaction rate within your organisation (as a percentage based on survey responses)?",
            name: "employee-satisfaction",
            info: 'Please report as a percentage. If you used a scale such as "not at all satisfied - somewhat dissatisfied - neither satisfied nor dissatisfied - satisfied - very satisfied", then give each a score (0, 1, 2, 3, 4) and then average your score and report as a %age. e.g. an average score of 3.2 would be 80%.'
        },
        {
            component: (
                <CFormGroup >
                    <DatePicker
                        maxDate={new Date()}
                        name="satisfaction-survey-date"
                        onChange={onChange}
                        value={values["satisfaction-survey-date"]}
                        className="w-100"
                        placeholderText={"Select date..."}
                        isClearable
                    />
                    {errors && <ErrorText value={get(errors, "satisfaction-survey-date")} />}
                </CFormGroup>
            ),
            label:
                "When was the last time you conducted an employee satisfaction survey?",
            name: "satisfaction-survey-date"
        },

    ],

});
